/**
 * Persistent voices (judgment call 42). A slot is one voice built once when audio starts: an oscillator and three
 * filtered layers of one looping noise source (low-pass, band-pass, high-pass), each with its own envelope, through
 * an optional panner. Sounds re-trigger its envelopes with scheduled automation, so playing a sound creates no audio
 * nodes and the game loop allocates nothing (GDD §10.5). Works on any BaseAudioContext (offline rendering in tests).
 */

/** Seconds ahead a new sound starts, so the voice's previous sound can fade out first (no click). */
export const LEAD = 0.008;

export interface Slot {
  /** Where the slot's layers meet: the panner, or a plain gain for sounds at the jet. */
  readonly out: AudioNode;
  readonly pan: PannerNode | null;
  readonly osc: OscillatorNode;
  readonly tone: GainNode;
  readonly low: BiquadFilterNode;
  readonly lowEnv: GainNode;
  readonly mid: BiquadFilterNode;
  readonly midEnv: GainNode;
  readonly high: BiquadFilterNode;
  readonly highEnv: GainNode;
  /** Audio time when the current sound has died away. */
  busyUntil: number;
  /** World position of a positional sound (tracked as the jet flies past it). */
  x: number;
  y: number;
  z: number;
  /** Position last sent to the panner, relative to the jet. */
  rx: number;
  ry: number;
  rz: number;
}

/** Two seconds of white noise, shared by every noise voice (each starts at its own offset). */
export function noiseBuffer(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  // A fixed generator, so offline renders in tests repeat exactly.
  let s = 0x2545f491;
  for (let i = 0; i < n; i++) {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    d[i] = ((s >>> 0) / 4294967296) * 2 - 1;
  }
  return buf;
}

/** A looping noise source started at once (it runs for the life of the context). */
export function noiseSource(ctx: BaseAudioContext, buf: AudioBuffer, offset: number): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.start(0, offset % buf.duration);
  return src;
}

export function silentGain(ctx: BaseAudioContext, dest: AudioNode | null, value = 0): GainNode {
  const g = ctx.createGain();
  g.gain.value = value;
  if (dest) g.connect(dest);
  return g;
}

/** A gain of `depth` feeding an audio parameter: an LFO through it modulates the parameter by ±depth. */
export function modulate(ctx: BaseAudioContext, param: AudioParam, depth: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = depth;
  g.connect(param);
  return g;
}

function filter(ctx: BaseAudioContext, type: BiquadFilterType, freq: number, q: number): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  return f;
}

/** Positional sources: equal-power panning, inverse distance falloff from 50 m (judgment call 43). */
export function panner(ctx: BaseAudioContext, dest: AudioNode): PannerNode {
  const p = ctx.createPanner();
  p.panningModel = 'equalpower';
  p.distanceModel = 'inverse';
  p.refDistance = 50;
  p.maxDistance = 10000;
  p.rolloffFactor = 1;
  p.connect(dest);
  return p;
}

export function createSlot(ctx: BaseAudioContext, noise: AudioBuffer, dest: AudioNode, spatial: boolean): Slot {
  const pan = spatial ? panner(ctx, dest) : null;
  const out: AudioNode = pan ?? silentGain(ctx, dest, 1);
  const osc = ctx.createOscillator();
  const tone = silentGain(ctx, out);
  osc.connect(tone);
  osc.start();
  const src = noiseSource(ctx, noise, Math.random() * noise.duration);
  const low = filter(ctx, 'lowpass', 400, 0.7);
  const mid = filter(ctx, 'bandpass', 1500, 1);
  const high = filter(ctx, 'highpass', 3000, 0.7);
  const lowEnv = silentGain(ctx, out);
  const midEnv = silentGain(ctx, out);
  const highEnv = silentGain(ctx, out);
  src.connect(low).connect(lowEnv);
  src.connect(mid).connect(midEnv);
  src.connect(high).connect(highEnv);
  return {
    out,
    pan,
    osc,
    tone,
    low,
    lowEnv,
    mid,
    midEnv,
    high,
    highEnv,
    busyUntil: 0,
    x: 0.5,
    y: 0.5,
    z: 0.5,
    rx: 0.5,
    ry: 0.5,
    rz: 0.5,
  };
}

/** Clears what a gain had scheduled from `now` and fades it out over a few milliseconds. */
export function release(p: AudioParam, now: number): void {
  p.cancelScheduledValues(now);
  p.setTargetAtTime(0, now, 0.002);
}

/**
 * A percussive envelope: from silence at `t`, up to `peak` in `attack` seconds, then an exponential fall that is
 * about 2% of the peak after `decay` seconds.
 */
export function strike(p: AudioParam, now: number, t: number, peak: number, attack: number, decay: number): void {
  release(p, now);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + attack);
  p.setTargetAtTime(0, t + attack, decay / 4);
}

/** A held envelope: up to `peak`, held for `hold` seconds, then a fall over `decay`. */
export function swell(
  p: AudioParam,
  now: number,
  t: number,
  peak: number,
  attack: number,
  hold: number,
  decay: number,
): void {
  release(p, now);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + attack);
  p.setValueAtTime(peak, t + attack + hold);
  p.setTargetAtTime(0, t + attack + hold, decay / 4);
}

/** A pitch (or filter) sweep: `from` at `t`, exponentially to `to` over `secs`. */
export function sweep(p: AudioParam, now: number, t: number, from: number, to: number, secs: number): void {
  p.cancelScheduledValues(now);
  p.setValueAtTime(from, t);
  p.exponentialRampToValueAtTime(to, t + secs);
}

/** A note at `t` in a sequence: fades the previous note first, up in `a`, then away with time constant `tc`. */
export function note(p: AudioParam, t: number, peak: number, a: number, tc: number): void {
  p.setTargetAtTime(0, Math.max(0, t - 0.006), 0.0015);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(peak, t + a);
  p.setTargetAtTime(0, t + a, tc);
}

/** Sets a value at `t`, replacing what was scheduled. */
export function setAt(p: AudioParam, now: number, t: number, v: number): void {
  p.cancelScheduledValues(now);
  p.setValueAtTime(v, t);
}

/** Moves a value smoothly toward `v` (time constant `tc`), for parameters driven every few frames. */
export function glide(p: AudioParam, now: number, v: number, tc: number): void {
  p.setTargetAtTime(v, now, tc);
}

/** A free slot, or the one closest to finishing (voice stealing). */
export function takeSlot(slots: readonly Slot[], now: number): Slot {
  let best = slots[0] as Slot;
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i] as Slot;
    if (s.busyUntil <= now) return s;
    if (s.busyUntil < best.busyUntil) best = s;
  }
  return best;
}
